import React, { FC, useState } from 'react';
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import { ApolloCache, Reference, useMutation, useQuery } from '@apollo/client';
import { getRolesTemplate } from 'apollo/templates/user/query.graphql';
import { addDentistTemplate } from 'apollo/templates/user/mutation.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Organization } from 'models/organization';
import { ClinicDropdown, Dentist, Role, User } from 'models/user';
import AuthMethodFields, {
    authMethodVariables,
    validateAuthMethodFields,
} from 'components/users/shared/auth-method-fields/auth-method-fields.component';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import Loader from 'components/shared/loader/loader.component';
import ClinicsAutocomplete from 'components/users/dentists/clinics-autocomplete/clinics-autocomplete.component';
import ChildClinicsAutocomplete from 'components/users/dentists/add-dentist-dialog/child-clinics-autocomplete.component';
import { nameRegExp } from 'config/regex';

type AddDentistFormComponentProps = {
    email: string;
    onDialogClose: () => void;
    onUserAdded: (user: User) => void;
    /** The clinic picked in the dialog's first step; can still be changed here. */
    organization: ClinicDropdown | null;
};
const AddDentistFormComponent: FC<AddDentistFormComponentProps> = ({
    email,
    onDialogClose,
    onUserAdded,
    organization,
}: AddDentistFormComponentProps) => {
    const { t: translate } = useTranslation(['common', 'users', 'organization']);
    const [isFirstNameValid, setIsFirstNameValid] = useState<boolean>(false);
    const [isLastNameValid, setIsLastNameValid] = useState<boolean>(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [addDentist] = useMutation(addDentistTemplate, {
        onCompleted: ({ addDentist }) => {
            onUserAdded(addDentist);
            onDialogClose();
        },
        onError: (err) => {
            showBoundary(err);
        },
        update(cache: ApolloCache<User>, { data: { addDentist } }) {
            cache.modify({
                fields: {
                    dentists(dentistRefs: Reference | (Reference | undefined)[], { toReference }) {
                        if (dentistRefs instanceof Array) {
                            return [...dentistRefs, toReference(addDentist)];
                        }
                        return [dentistRefs, toReference(addDentist)];
                    },
                },
            });
        },
    });

    const { data: { roles } = {}, loading: rolesLoading } = useQuery(getRolesTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: { type: 'DENTIST' },
        onError(error) {
            showBoundary(error);
        },
    });

    const validate = (values: Dentist) => {
        const errors: Record<string, string> = {};
        if (!values.firstName) {
            errors.firstName = translate('users:VALIDATION:FIRST_NAME');
        }
        if (values.firstName && !isFirstNameValid) {
            errors.firstName = translate('common:VALIDATION.INCORRECT_NAME');
        }
        if (!values.lastName) {
            errors.lastName = translate('users:VALIDATION:LAST_NAME');
        }
        if (values.lastName && !isLastNameValid) {
            errors.lastName = translate('common:VALIDATION.INCORRECT_NAME');
        }
        if (!values.email) {
            errors.email = translate('users:VALIDATION:EMAIL');
        }
        if (!values.role) {
            errors.role = translate('users:VALIDATION:ROLE');
        }
        validateAuthMethodFields(values, translate, errors);
        if (!values.organization) {
            errors.organization = translate('organization:VALIDATION:PARENT_CLINIC');
        }
        return errors;
    };
    const formik = useFormik({
        initialValues: {
            firstName: '',
            title: '',
            lastName: '',
            email: email,
            role: '',
            authMethod: undefined,
            syncid: '',
            personalIdentityNumber: '',
            organization: (organization as Organization | null) ?? '',
            defaultClinic: '',
            isActive: undefined,
            enableUserSync: false,
        },
        validateOnMount: true,
        validate,
        enableReinitialize: true,
        onSubmit: async (values: Dentist) => {
            const organizationId = (values.organization as ClinicDropdown)?._id;
            await addDentist({
                variables: {
                    name: { firstName: values.firstName, lastName: values.lastName },
                    email: values.email,
                    role: values.role,
                    ...authMethodVariables(values),
                    organization: organizationId,
                    // Without a pick among the clinic's delivery locations, the clinic itself.
                    defaultClinic: (values.defaultClinic as ClinicDropdown)?._id || organizationId,
                    title: values.title,
                },
            });
        },
    });

    const handleParentClinicChange = (clinic?: ClinicDropdown | null) => {
        formik.setFieldValue('organization', clinic);
        formik.setFieldValue('defaultClinic', '');
    };

    const handleChildClinicChange = (clinic?: ClinicDropdown | null) => {
        formik.setFieldValue('defaultClinic', clinic);
    };

    const handleFirstNameChange = (newFirstName: string) => {
        const firstName = newFirstName.charAt(0).toUpperCase() + newFirstName.slice(1);
        setIsFirstNameValid(nameRegExp.test(firstName));
        formik.setFieldValue('firstName', firstName);
    };

    const handleLastNameChange = (newLastName: string) => {
        const lastName = newLastName.charAt(0).toUpperCase() + newLastName.slice(1);
        setIsLastNameValid(nameRegExp.test(lastName));
        formik.setFieldValue('lastName', lastName);
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <DialogContent>
                <Box mt={1}>
                    {rolesLoading ? (
                        <Loader />
                    ) : (
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <FormControl margin="dense" fullWidth>
                                    <TextField
                                        id="first-name"
                                        label={translate('common:FIRST_NAME')}
                                        required
                                        helperText={formik.touched.firstName && formik.errors.firstName}
                                        error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                                        onChange={(e) => handleFirstNameChange(e.target.value)}
                                        onBlur={formik.getFieldProps('firstName').onBlur}
                                        name={formik.getFieldProps('firstName').name}
                                        value={formik.getFieldProps('firstName').value}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl margin="dense" fullWidth>
                                    <TextField
                                        id="last-name"
                                        label={translate('common:LAST_NAME')}
                                        required
                                        helperText={formik.touched.lastName && formik.errors.lastName}
                                        error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                                        onChange={(e) => handleLastNameChange(e.target.value)}
                                        onBlur={formik.getFieldProps('lastName').onBlur}
                                        name={formik.getFieldProps('lastName').name}
                                        value={formik.getFieldProps('lastName').value}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={6}>
                                <FormControl margin="dense" fullWidth>
                                    <TextField
                                        id="email"
                                        label={translate('common:EMAIL')}
                                        required
                                        disabled
                                        type="email"
                                        helperText={formik.touched.email && formik.errors.email}
                                        error={formik.touched.email && Boolean(formik.errors.email)}
                                        {...formik.getFieldProps('email')}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl
                                    margin="dense"
                                    fullWidth
                                    error={formik.touched.role && Boolean(formik.errors.role)}
                                    required
                                >
                                    <InputLabel id="role-select-label">{translate('common:ROLE')}</InputLabel>
                                    <Select
                                        labelId="role-select-label"
                                        id="role-select"
                                        label={translate('common:ROLE')}
                                        required
                                        error={formik.touched.role && Boolean(formik.errors.role)}
                                        {...formik.getFieldProps('role')}
                                    >
                                        {roles.map((role: Role) => (
                                            <MenuItem key={role._id} value={role.role}>
                                                {translate(`common:${role.role}`)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText error={formik.touched.role && Boolean(formik.errors.role)}>
                                        {formik.touched.role && formik.errors.role}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <ClinicsAutocomplete
                                        onParentClinicChange={handleParentClinicChange}
                                        value={(formik.values.organization as ClinicDropdown) || null}
                                        name="organization"
                                        onBlur={formik.handleBlur}
                                        helperText={formik.touched.organization && formik.errors.organization}
                                        error={formik.touched.organization && Boolean(formik.errors.organization)}
                                        fullWidth
                                        useInForm
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl margin="dense" fullWidth>
                                    <ChildClinicsAutocomplete
                                        key={(formik.values.organization as ClinicDropdown)?._id}
                                        parentClinic={formik.values.organization as ClinicDropdown}
                                        onChildClinicChange={handleChildClinicChange}
                                        value={(formik.values.defaultClinic as ClinicDropdown) || null}
                                        fullWidth
                                        useInForm
                                    />
                                </FormControl>
                            </Grid>
                            <AuthMethodFields formik={formik} />
                            <Grid item xs={6}>
                                <FormControl margin="dense" fullWidth>
                                    <TextField
                                        id="title"
                                        label={translate('common:TITLE')}
                                        helperText={formik.touched.title && formik.errors.title}
                                        error={formik.touched.title && Boolean(formik.errors.title)}
                                        {...formik.getFieldProps('title')}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onDialogClose}>{translate('common:CANCEL')}</Button>
                <Button disabled={!formik.isValid} variant="contained" type="submit">
                    {translate('common:SAVE')}
                </Button>
            </DialogActions>
        </form>
    );
};

export default AddDentistFormComponent;
